import { useEffect, useRef, useState } from "react";
import { Record, Stop, X, UploadSimple, CheckCircle, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function ScreenRecorder() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | preview | uploading | done
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState(0);
  const [title, setTitle] = useState("");
  const [blobUrl, setBlobUrl] = useState("");
  const [topics, setTopics] = useState([]);
  const [attachTopic, setAttachTopic] = useState("");
  const [doneUrl, setDoneUrl] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const blobRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      let mic = null;
      try { mic = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { /* mic optional */ }
      const audioTracks = display.getAudioTracks().length ? display.getAudioTracks() : (mic ? mic.getAudioTracks() : []);
      const stream = new MediaStream([...display.getVideoTracks(), ...audioTracks]);
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm" });
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        blobRef.current = blob;
        setBlobUrl(URL.createObjectURL(blob));
        setPhase("preview");
      };
      display.getVideoTracks()[0].onended = () => stop();
      recRef.current = rec;
      rec.start(1000);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setPhase("recording");
    } catch (e) {
      if (e?.name !== "NotAllowedError") toast.error("Screen recording failed to start");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recRef.current?.stop(); } catch { /* noop */ }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const reset = () => {
    stop();
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(""); blobRef.current = null;
    setPhase("idle"); setProgress(0); setTitle(""); setDoneUrl(""); setAttachTopic("");
  };

  const upload = async () => {
    const blob = blobRef.current;
    if (!blob) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const { data: start } = await api.post("/recordings/start", { title: title.trim() || `Screen recording ${new Date().toLocaleString()}` });
      const chunkSize = start.chunk_size;
      const chunks = Math.ceil(blob.size / chunkSize);
      for (let i = 0; i < chunks; i++) {
        await api.post(`/recordings/chunk?id=${start.id}&index=${i}`, blob.slice(i * chunkSize, (i + 1) * chunkSize),
          { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 });
        setProgress(Math.round(((i + 1) / chunks) * 100));
      }
      const { data: fin } = await api.post("/recordings/finish", { id: start.id, title: title.trim() || undefined });
      setDoneUrl(fin.url);
      setDoneId(fin.id);
      const { data: t } = await api.get("/training/topics");
      setTopics(Array.isArray(t.data) ? t.data : (t.data?.topics || []));
      setPhase("done");
      toast.success("Recording saved");
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("preview");
    }
  };

  const [doneId, setDoneId] = useState("");

  const attach = async () => {
    if (!attachTopic) { toast.error("Pick a training topic"); return; }
    try {
      const { data } = await api.post(`/recordings/${doneId}/attach`, { topic_id: attachTopic });
      toast.success(`Attached to "${data.topic}" — it now plays inside that training`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-[#C0392B] text-white shadow-lg transition-transform hover:scale-105"
        title="Record my screen" data-testid="screen-record-fab">
        <Record size={20} weight="fill" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="screen-record-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Screen recorder</h2>
              <button onClick={() => { reset(); setOpen(false); }} className="text-stone-400 hover:text-stone-700" data-testid="screen-record-close"><X size={18} /></button>
            </div>
            <p className="mt-1 text-xs text-stone-500">Records your screen + voice, saves into the app, and can attach straight to a training topic as its video.</p>

            {phase === "idle" && (
              <div className="mt-5 space-y-4">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g., How to clock in on HHAeXchange)"
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="screen-record-title" />
                <Button onClick={start} className="w-full rounded-full bg-[#C0392B] text-white hover:bg-stone-900" data-testid="screen-record-start">
                  <Record size={16} weight="fill" className="mr-1.5" /> Start recording
                </Button>
                <p className="text-[11px] text-stone-400">Your browser will ask which screen or window to share. Stop anytime with the button or the browser's own stop.</p>
              </div>
            )}

            {phase === "recording" && (
              <div className="mt-5 space-y-4 text-center">
                <p className="flex items-center justify-center gap-2 text-2xl font-bold text-stone-900">
                  <span className="h-3 w-3 animate-pulse rounded-full bg-[#C0392B]" data-testid="screen-record-indicator" /> {fmtTime(elapsed)}
                </p>
                <Button onClick={stop} className="w-full rounded-full bg-stone-900 text-white" data-testid="screen-record-stop">
                  <Stop size={16} weight="fill" className="mr-1.5" /> Stop recording
                </Button>
              </div>
            )}

            {phase === "preview" && (
              <div className="mt-5 space-y-4">
                <video src={blobUrl} controls className="w-full rounded-xl border border-stone-200 bg-black" data-testid="screen-record-preview" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} className="flex-1 border-stone-300 text-stone-500" data-testid="screen-record-discard">Discard & re-record</Button>
                  <Button onClick={upload} className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="screen-record-upload">
                    <UploadSimple size={15} className="mr-1.5" /> Save to app
                  </Button>
                </div>
              </div>
            )}

            {phase === "uploading" && (
              <div className="mt-5 space-y-3" data-testid="screen-record-uploading">
                <p className="text-sm font-medium text-stone-700">Saving… {progress}%</p>
                <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {phase === "done" && (
              <div className="mt-5 space-y-4" data-testid="screen-record-done">
                <p className="flex items-center gap-2 text-sm font-medium text-[#1e8449]"><CheckCircle size={18} weight="fill" /> Saved to the app.</p>
                <div>
                  <p className="text-xs font-semibold text-stone-700">Attach to a training topic (becomes its training video)</p>
                  <div className="mt-1.5 flex gap-2">
                    <select value={attachTopic} onChange={(e) => setAttachTopic(e.target.value)}
                      className="h-9 min-w-0 flex-1 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="screen-record-attach-select">
                      <option value="">Choose training…</option>
                      {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                    </select>
                    <Button size="sm" onClick={attach} disabled={!attachTopic} className="rounded-full bg-primary text-primary-foreground" data-testid="screen-record-attach">Attach</Button>
                  </div>
                </div>
                <Button variant="outline" onClick={() => { reset(); setOpen(false); }} className="w-full border-stone-300 text-stone-500" data-testid="screen-record-finish">Done</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
